import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { formatAppDate, parseAppDate } from '../utils/date';
import {
  X,
  Clock,
  User,
  Flag,
  Eye,
  FileText,
  Paperclip,
  MessageSquare,
  Upload,
  Trash2,
  Send,
  ChevronLeft,
  ChevronRight,
  Edit3,
  Save,
  Lock,
  Building2,
  ListTodo,
  Plus,
  Check,
  Circle,
} from 'lucide-react';

const STATUS_LABELS = {
  new: 'Mới',
  in_progress: 'Đang làm',
  on_hold: 'Đang treo',
  in_review: 'Đang Review',
  done: 'Đã xong',
  cancelled: 'Huỷ',
};
const PRIORITY_LABELS = { high: 'Cao', medium: 'Trung bình', low: 'Thấp' };
const VIS_LABELS = { public: 'Public', internal: 'Internal', private: 'Private' };

const normalizeRole = (role) => (role === 'checker' ? 'viewer' : role);

export default function TaskDetailModal({ taskId, onClose, onUpdated, companyOptions: fallbackCompanyOptions = [], assigneeSuggestions = [] }) {
  const auth = useAuth();
  const { user, isAdmin, isManager, canComment } = auth;
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [comments, setComments] = useState([]);
  const [commentPagination, setCommentPagination] = useState({ page: 1, total_pages: 1 });
  const [newComment, setNewComment] = useState('');
  const [activeTab, setActiveTab] = useState('detail');
  const [deleting, setDeleting] = useState(false);
  const [progressNote, setProgressNote] = useState('');
  const [savedProgressNote, setSavedProgressNote] = useState('');
  const [progressTodos, setProgressTodos] = useState([]);
  const [newTodoText, setNewTodoText] = useState('');
  const [progressSaving, setProgressSaving] = useState(false);
  const [progressError, setProgressError] = useState('');
  const fileInputRef = useRef(null);
  const progressNoteRef = useRef('');
  const savedProgressNoteRef = useRef('');
  const progressTodosRef = useRef([]);
  const newTodoTextRef = useRef('');
  const deliverableFileRef = useRef(null);
  const assigneeDatalistId = useMemo(() => `assignee-suggest-${taskId}`, [taskId]);

  useEffect(() => {
    progressNoteRef.current = progressNote;
  }, [progressNote]);

  useEffect(() => {
    savedProgressNoteRef.current = savedProgressNote;
  }, [savedProgressNote]);

  useEffect(() => {
    progressTodosRef.current = progressTodos;
  }, [progressTodos]);

  useEffect(() => {
    newTodoTextRef.current = newTodoText;
  }, [newTodoText]);

  const fetchTask = useCallback(async ({ preserveDraft = false } = {}) => {
    try {
      const data = await api.getTask(taskId);
      setTask(data.task);
      if (data.task && !data.task.is_masked) {
        if (!preserveDraft) {
          setEditData({
            title: data.task.title,
            description: data.task.description || '',
            assigned_by: data.task.assigned_by || '',
            priority: data.task.priority,
            visibility: data.task.visibility,
            status: data.task.status,
            deadline: data.task.deadline ? data.task.deadline.slice(0, 16) : '',
            task_company: data.task.task_company || '',
          });
        }

        const incomingNote = data.task.progress_note || '';
        const hasDirtyTodo = progressTodosRef.current.some((todo) => todo._dirty || todo._saving);

        if (!preserveDraft && progressNoteRef.current === savedProgressNoteRef.current) {
          setProgressNote(incomingNote);
          setSavedProgressNote(incomingNote);
        }

        if (!preserveDraft && !hasDirtyTodo && !newTodoTextRef.current.trim()) {
          setProgressTodos((data.task.progress_todos || []).map((todo) => ({ ...todo, _dirty: false, _saving: false })));
        }

        setProgressError('');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  const fetchComments = useCallback(async (page = 1) => {
    try {
      const data = await api.getComments(taskId, page);
      setComments(data.comments || []);
      setCommentPagination(data.pagination || { page: 1, total_pages: 1 });
    } catch (err) {
      console.error(err);
    }
  }, [taskId]);

  useEffect(() => {
    fetchTask();
    fetchComments();
  }, [taskId]);

  useEffect(() => {
    const shouldPausePolling = editing || deleting || progressSaving || newComment.trim() !== '' || newTodoText.trim() !== '' || progressNote !== savedProgressNote || progressTodos.some((todo) => todo._dirty || todo._saving);
    if (shouldPausePolling) {
      return undefined;
    }

    const poll = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      fetchTask({ preserveDraft: false });
      fetchComments(commentPagination.page || 1);
    };

    const interval = window.setInterval(poll, 5000);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        poll();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [commentPagination.page, deleting, editing, fetchComments, fetchTask, newComment, newTodoText, progressNote, progressSaving, progressTodos, savedProgressNote]);

  const role = normalizeRole(user?.role);
  const canEdit = useMemo(() => {
    if (!task) return false;
    if (isAdmin) return true;
    return isManager && task.visibility !== 'private' && !['done', 'cancelled'].includes(task.status);
  }, [task, isAdmin, isManager]);
  const canDelete = isAdmin;
  const canManageAttachments = canEdit;
  const companyOptions = useMemo(() => (task?.company_options?.length ? task.company_options : fallbackCompanyOptions), [task?.company_options, fallbackCompanyOptions]);
  const canManageProgress = Boolean(isAdmin && task && !task.is_masked);

  useEffect(() => {
    if (!canManageProgress) return undefined;
    if (progressNote === savedProgressNote) return undefined;

    setProgressSaving(true);
    const timeout = setTimeout(async () => {
      try {
        const data = await api.saveTaskProgressNote(taskId, progressNote);
        setSavedProgressNote(data.note ?? progressNote);
        setProgressError('');
      } catch (err) {
        setProgressError(err.message);
      } finally {
        setProgressSaving(false);
      }
    }, 650);

    return () => clearTimeout(timeout);
  }, [canManageProgress, progressNote, savedProgressNote, taskId]);

  useEffect(() => {
    if (!canManageProgress) return undefined;
    const dirtyTodo = progressTodos.find((todo) => todo.id && todo._dirty && !todo._saving);
    if (!dirtyTodo) return undefined;

    const timeout = setTimeout(async () => {
      setProgressTodos((prev) => prev.map((todo) => (todo.id === dirtyTodo.id ? { ...todo, _saving: true } : todo)));
      try {
        const data = await api.updateTaskProgressTodo(taskId, dirtyTodo.id, { text: dirtyTodo.todo_text });
        setProgressTodos((prev) => prev.map((todo) => {
          if (todo.id !== dirtyTodo.id) return todo;
          return { ...(data.todo || todo), _dirty: false, _saving: false };
        }));
        setProgressError('');
      } catch (err) {
        setProgressTodos((prev) => prev.map((todo) => (todo.id === dirtyTodo.id ? { ...todo, _saving: false } : todo)));
        setProgressError(err.message);
      }
    }, 550);

    return () => clearTimeout(timeout);
  }, [canManageProgress, progressTodos, taskId]);

  const handleCreateProgressTodo = async () => {
    const value = newTodoText.trim();
    if (!value || !canManageProgress) return;
    try {
      const data = await api.createTaskProgressTodo(taskId, value);
      setProgressTodos((prev) => [...prev, { ...(data.todo || {}), _dirty: false, _saving: false }]);
      setNewTodoText('');
      setProgressError('');
    } catch (err) {
      setProgressError(err.message);
    }
  };

  const handleToggleProgressTodo = async (todo) => {
    if (!canManageProgress || !todo?.id) return;
    setProgressTodos((prev) => prev.map((item) => (item.id === todo.id ? { ...item, is_done: !item.is_done, _saving: true } : item)));
    try {
      const data = await api.updateTaskProgressTodo(taskId, todo.id, { is_done: !todo.is_done });
      setProgressTodos((prev) => prev.map((item) => (item.id === todo.id ? { ...(data.todo || item), _dirty: false, _saving: false } : item)));
      setProgressError('');
    } catch (err) {
      setProgressTodos((prev) => prev.map((item) => (item.id === todo.id ? { ...item, is_done: todo.is_done, _saving: false } : item)));
      setProgressError(err.message);
    }
  };

  const handleUpdateProgressTodoText = (todoId, value) => {
    setProgressTodos((prev) => prev.map((item) => (item.id === todoId ? { ...item, todo_text: value, _dirty: true } : item)));
  };

  const handleDeleteProgressTodo = async (todoId) => {
    if (!canManageProgress || !todoId) return;
    const previous = progressTodos;
    setProgressTodos((prev) => prev.filter((item) => item.id !== todoId));
    try {
      await api.deleteTaskProgressTodo(taskId, todoId);
      setProgressError('');
    } catch (err) {
      setProgressTodos(previous);
      setProgressError(err.message);
    }
  };

  const handleProgressTodoKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCreateProgressTodo();
    }
  };

  const handleSave = async () => {
    try {
      await api.updateTask(taskId, editData);
      setEditing(false);
      await fetchTask();
      onUpdated?.();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Bạn có chắc muốn xoá task này? Toàn bộ comment, resource, deliverable, dữ liệu liên quan và file đính kèm của task sẽ bị xoá.')) return;
    setDeleting(true);
    try {
      await api.deleteTask(taskId);
      onUpdated?.();
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.addComment(taskId, newComment.trim());
      setNewComment('');
      fetchComments(1);
      fetchTask();
      onUpdated?.();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await api.deleteComment(commentId);
      fetchComments(commentPagination.page);
      fetchTask();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUploadResource = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await api.addResourceFile(taskId, file);
      fetchTask();
      onUpdated?.();
    } catch (err) {
      alert(err.message);
    }
    e.target.value = '';
  };

  const handleUploadDeliverable = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await api.addDeliverableFile(taskId, file);
      fetchTask();
      onUpdated?.();
    } catch (err) {
      alert(err.message);
    }
    e.target.value = '';
  };

  const handleAddResourceText = async () => {
    const text = window.prompt('Nhập nội dung tài nguyên:');
    if (!text) return;
    try {
      await api.addResourceText(taskId, text);
      fetchTask();
      onUpdated?.();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddDeliverableText = async () => {
    const text = window.prompt('Nhập nội dung thành phẩm:');
    if (!text) return;
    try {
      await api.addDeliverableText(taskId, text);
      fetchTask();
      onUpdated?.();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRemoveResource = async (id) => {
    try {
      await api.removeResource(id);
      fetchTask();
      onUpdated?.();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRemoveDeliverable = async (id) => {
    try {
      await api.removeDeliverable(id);
      fetchTask();
      onUpdated?.();
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
          <div className="spinner-wrap"><div className="spinner" /></div>
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
          <div className="modal-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            Task không tồn tại hoặc bạn không có quyền xem.
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={onClose}>Đóng</button>
          </div>
        </div>
      </div>
    );
  }

  if (task.is_masked) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Lock size={18} /> {task.title}
            </div>
            <button className="btn-icon" onClick={onClose} type="button"><X size={18} /></button>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
            {task.visibility === 'private'
              ? 'Task này là Private. Chỉ Admin mới có thể xem nội dung.'
              : 'Task này là Internal. Bạn cần đăng nhập để xem chi tiết.'}
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (value) => {
    const date = parseAppDate(value);
    if (!date) return '—';
    return formatAppDate(date);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        {deleting && (
          <div className="blocking-overlay">
            <div className="blocking-card">
              <div className="spinner" />
              <span>Đang xoá task và dữ liệu liên quan...</span>
            </div>
          </div>
        )}

        <div className="modal-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            {editing ? (
              <input className="form-input" value={editData.title} onChange={(e) => setEditData({ ...editData, title: e.target.value })} style={{ fontSize: 18, fontWeight: 700 }} />
            ) : (
              <div className="modal-title">{task.title}</div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Tạo lúc {formatDate(task.created_at)}</div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {canEdit && !editing && (
              <button className="btn-icon" onClick={() => setEditing(true)} title="Chỉnh sửa" type="button">
                <Edit3 size={16} />
              </button>
            )}
            {editing && (
              <>
                <button className="btn btn-primary btn-sm" onClick={handleSave} type="button"><Save size={14} /> Lưu</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)} type="button">Huỷ</button>
              </>
            )}
            <button className="btn-icon" onClick={onClose} type="button"><X size={18} /></button>
          </div>
        </div>

        <div className="tabs padded-tabs">
          <button className={`tab ${activeTab === 'detail' ? 'active' : ''}`} onClick={() => setActiveTab('detail')} type="button">Chi tiết</button>
          <button className={`tab ${activeTab === 'resources' ? 'active' : ''}`} onClick={() => setActiveTab('resources')} type="button">Tài nguyên {task.resources?.length ? `(${task.resources.length})` : ''}</button>
          <button className={`tab ${activeTab === 'deliverables' ? 'active' : ''}`} onClick={() => setActiveTab('deliverables')} type="button">Thành phẩm {task.deliverables?.length ? `(${task.deliverables.length})` : ''}</button>
          <button className={`tab ${activeTab === 'comments' ? 'active' : ''}`} onClick={() => setActiveTab('comments')} type="button">Bình luận {task.comment_count ? `(${task.comment_count})` : ''}</button>
        </div>

        <div className="modal-body">
          {activeTab === 'detail' && (
            <div className="task-detail-grid">
              <div className="task-detail-main">
                <div className="detail-section">
                  <div className="detail-section-title"><FileText size={14} /> Mô tả</div>
                  {editing ? (
                    <textarea className="form-textarea" rows={6} value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })} placeholder="Nhập mô tả task..." />
                  ) : (
                    <div className="detail-description">{task.description || <em style={{ color: 'var(--text-muted)' }}>Chưa có mô tả</em>}</div>
                  )}
                </div>

                <div className="detail-section progress-section">
                  <div className="detail-section-title"><ListTodo size={14} /> Update tiến độ</div>
                  {progressError && <div className="alert alert-error" style={{ marginBottom: 12 }}>{progressError}</div>}

                  <div className="progress-note-block">
                    <div className="progress-subtitle">Ghi chú tiến độ</div>
                    {canManageProgress ? (
                      <>
                        <textarea
                          className="form-textarea progress-note-input"
                          rows={5}
                          value={progressNote}
                          onChange={(e) => setProgressNote(e.target.value)}
                          placeholder="Nhập cập nhật tiến độ... hệ thống sẽ tự lưu khi bạn dừng nhập."
                        />
                        <div className="progress-save-hint">{progressSaving ? 'Đang tự lưu...' : 'Tự động lưu khi bạn nhập xong'}</div>
                      </>
                    ) : (
                      <div className="detail-description">{progressNote || <em style={{ color: 'var(--text-muted)' }}>Chưa có cập nhật tiến độ</em>}</div>
                    )}
                  </div>

                  <div className="progress-todo-block">
                    <div className="progress-subtitle">Todo tiến độ</div>
                    {canManageProgress && (
                      <div className="progress-add-row">
                        <input
                          className="form-input"
                          value={newTodoText}
                          onChange={(e) => setNewTodoText(e.target.value)}
                          onKeyDown={handleProgressTodoKeyDown}
                          placeholder="Thêm todo mới..."
                        />
                        <button className="btn btn-ghost btn-sm" type="button" onClick={handleCreateProgressTodo}>
                          <Plus size={14} /> Thêm
                        </button>
                      </div>
                    )}

                    <div className="progress-todo-list">
                      {progressTodos.length === 0 && <div className="empty-state">Chưa có todo tiến độ</div>}
                      {progressTodos.map((todo) => (
                        <div className={`progress-todo-item ${todo.is_done ? 'done' : ''}`} key={todo.id}>
                          <button
                            type="button"
                            className={`progress-todo-check ${todo.is_done ? 'checked' : ''}`}
                            onClick={() => handleToggleProgressTodo(todo)}
                            disabled={!canManageProgress || todo._saving}
                            title={todo.is_done ? 'Đánh dấu chưa xong' : 'Đánh dấu đã xong'}
                          >
                            {todo.is_done ? <Check size={14} /> : <Circle size={14} />}
                          </button>

                          {canManageProgress ? (
                            <input
                              className="progress-todo-input"
                              value={todo.todo_text}
                              onChange={(e) => handleUpdateProgressTodoText(todo.id, e.target.value)}
                              placeholder="Nội dung todo..."
                            />
                          ) : (
                            <div className="progress-todo-text">{todo.todo_text}</div>
                          )}

                          {canManageProgress && (
                            <button className="btn-icon danger-icon" type="button" onClick={() => handleDeleteProgressTodo(todo.id)} title="Xoá todo">
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="task-detail-sidebar">
                <div className="detail-meta-item">
                  <span className="detail-meta-label">Trạng thái</span>
                  {editing ? (
                    <select className="form-select" value={editData.status} onChange={(e) => setEditData({ ...editData, status: e.target.value })} style={{ flex: 1 }}>
                      {Object.entries(STATUS_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select>
                  ) : (
                    <span className="detail-meta-value">{STATUS_LABELS[task.status]}</span>
                  )}
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Ưu tiên</span>
                  {editing ? (
                    <select className="form-select" value={editData.priority} onChange={(e) => setEditData({ ...editData, priority: e.target.value })} style={{ flex: 1 }}>
                      <option value="high">Cao</option>
                      <option value="medium">Trung bình</option>
                      <option value="low">Thấp</option>
                    </select>
                  ) : (
                    <span className={`badge badge-priority-${task.priority}`}>{PRIORITY_LABELS[task.priority]}</span>
                  )}
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Giao bởi</span>
                  {editing ? (
                    <>
                      <input className="form-input" value={editData.assigned_by} onChange={(e) => setEditData({ ...editData, assigned_by: e.target.value })} style={{ flex: 1 }} list={assigneeDatalistId} />
                      <datalist id={assigneeDatalistId}>
                        {assigneeSuggestions.map((name) => <option key={name} value={name} />)}
                      </datalist>
                    </>
                  ) : (
                    <span className="detail-meta-value"><User size={13} style={{ marginRight: 4 }} /> {task.assigned_by}</span>
                  )}
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Task thuộc công ty</span>
                  {editing ? (
                    <select className="form-select" value={editData.task_company || ''} onChange={(e) => setEditData({ ...editData, task_company: e.target.value })} style={{ flex: 1 }}>
                      <option value="">Không chọn</option>
                      {companyOptions.map((item) => <option key={item} value={item}>{item}</option>)}
                    </select>
                  ) : (
                    <span className="detail-meta-value">
                      <Building2 size={13} style={{ marginRight: 4 }} /> {task.task_company || '—'}
                    </span>
                  )}
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Hiển thị</span>
                  {editing ? (
                    <select className="form-select" value={editData.visibility} onChange={(e) => setEditData({ ...editData, visibility: e.target.value })} style={{ flex: 1 }}>
                      <option value="public">Public</option>
                      <option value="internal">Internal</option>
                      {isAdmin && <option value="private">Private</option>}
                    </select>
                  ) : (
                    <span className={`badge badge-vis-${task.visibility}`}>{VIS_LABELS[task.visibility]}</span>
                  )}
                </div>

                <div className="detail-meta-item">
                  <span className="detail-meta-label">Deadline</span>
                  {editing ? (
                    <input type="datetime-local" className="form-input" value={editData.deadline} onChange={(e) => setEditData({ ...editData, deadline: e.target.value })} style={{ flex: 1 }} />
                  ) : (
                    <span className="detail-meta-value"><Clock size={13} style={{ marginRight: 4 }} /> {task.deadline ? formatDate(task.deadline) : '—'}</span>
                  )}
                </div>

                {canDelete && !editing && (
                  <button className="btn btn-danger btn-sm" onClick={handleDelete} style={{ marginTop: 8 }} type="button">
                    <Trash2 size={14} /> Xoá task
                  </button>
                )}
              </div>
            </div>
          )}

          {activeTab === 'resources' && (
            <div>
              <div className="detail-section-title"><Paperclip size={14} /> Tài nguyên Task</div>
              {canManageAttachments && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleUploadResource} />
                  <button className="btn btn-ghost btn-sm" onClick={() => fileInputRef.current?.click()} type="button"><Upload size={14} /> Upload file</button>
                  <button className="btn btn-ghost btn-sm" onClick={handleAddResourceText} type="button"><FileText size={14} /> Thêm text</button>
                </div>
              )}
              <div className="attachment-list">
                {(!task.resources || task.resources.length === 0) && <div className="empty-state">Chưa có tài nguyên</div>}
                {task.resources?.map((resource) => (
                  <div key={resource.id} className={resource.type === 'file' ? 'attachment-item' : 'attachment-text'}>
                    {resource.type === 'file' ? (
                      <>
                        <Paperclip size={14} />
                        <a href={`/${resource.file_path}`} target="_blank" rel="noopener noreferrer">{resource.file_name}</a>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{(resource.file_size / 1024).toFixed(0)} KB</span>
                        {canManageAttachments && <button className="btn-icon" style={{ width: 24, height: 24 }} onClick={() => handleRemoveResource(resource.id)} type="button"><Trash2 size={12} /></button>}
                      </>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        {resource.text_content}
                        {canManageAttachments && <button className="btn-icon" style={{ position: 'absolute', top: -4, right: -4, width: 20, height: 20 }} onClick={() => handleRemoveResource(resource.id)} type="button"><Trash2 size={11} /></button>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'deliverables' && (
            <div>
              <div className="detail-section-title"><Flag size={14} /> Thành phẩm Task</div>
              {canManageAttachments && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <input type="file" ref={deliverableFileRef} style={{ display: 'none' }} onChange={handleUploadDeliverable} />
                  <button className="btn btn-ghost btn-sm" onClick={() => deliverableFileRef.current?.click()} type="button"><Upload size={14} /> Upload file</button>
                  <button className="btn btn-ghost btn-sm" onClick={handleAddDeliverableText} type="button"><FileText size={14} /> Thêm text</button>
                </div>
              )}
              <div className="attachment-list">
                {(!task.deliverables || task.deliverables.length === 0) && <div className="empty-state">Chưa có thành phẩm</div>}
                {task.deliverables?.map((deliverable) => (
                  <div key={deliverable.id} className={deliverable.type === 'file' ? 'attachment-item' : 'attachment-text'}>
                    {deliverable.type === 'file' ? (
                      <>
                        <Paperclip size={14} />
                        <a href={`/${deliverable.file_path}`} target="_blank" rel="noopener noreferrer">{deliverable.file_name}</a>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{(deliverable.file_size / 1024).toFixed(0)} KB</span>
                        {canManageAttachments && <button className="btn-icon" style={{ width: 24, height: 24 }} onClick={() => handleRemoveDeliverable(deliverable.id)} type="button"><Trash2 size={12} /></button>}
                      </>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        {deliverable.text_content}
                        {canManageAttachments && <button className="btn-icon" style={{ position: 'absolute', top: -4, right: -4, width: 20, height: 20 }} onClick={() => handleRemoveDeliverable(deliverable.id)} type="button"><Trash2 size={11} /></button>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'comments' && (
            <div>
              <div className="detail-section-title"><MessageSquare size={14} /> Bình luận</div>
              {canComment && (
                <div className="comment-input-wrapper" style={{ marginBottom: 16 }}>
                  <textarea className="form-textarea" rows={2} value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Viết bình luận..." onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleAddComment(); }} />
                  <button className="btn btn-primary btn-sm" onClick={handleAddComment} style={{ alignSelf: 'flex-end' }} type="button"><Send size={14} /></button>
                </div>
              )}

              {comments.length === 0 && <div className="empty-state">Chưa có bình luận</div>}
              {comments.map((comment) => (
                <div key={comment.id} className="comment-item">
                  <div className="comment-header">
                    <div className="comment-avatar">{comment.display_name?.charAt(0)?.toUpperCase()}</div>
                    <span className="comment-author">{comment.display_name}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', background: 'var(--bg-card)', padding: '1px 6px', borderRadius: 4 }}>{normalizeRole(comment.user_role)}</span>
                    <span className="comment-time">{formatDate(comment.created_at)}</span>
                    {(isAdmin || (user && user.id === comment.user_id)) && <button className="btn-icon" style={{ width: 20, height: 20, marginLeft: 'auto' }} onClick={() => handleDeleteComment(comment.id)} type="button"><Trash2 size={11} /></button>}
                  </div>
                  <div className="comment-body">{comment.content}</div>
                </div>
              ))}

              {commentPagination.total_pages > 1 && (
                <div className="pagination">
                  <button className="pagination-btn" disabled={commentPagination.page <= 1} onClick={() => fetchComments(commentPagination.page - 1)} type="button"><ChevronLeft size={14} /></button>
                  {Array.from({ length: commentPagination.total_pages }, (_, index) => index + 1).map((page) => (
                    <button key={page} className={`pagination-btn ${page === commentPagination.page ? 'active' : ''}`} onClick={() => fetchComments(page)} type="button">{page}</button>
                  ))}
                  <button className="pagination-btn" disabled={commentPagination.page >= commentPagination.total_pages} onClick={() => fetchComments(commentPagination.page + 1)} type="button"><ChevronRight size={14} /></button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
